import React, {useEffect, useState} from 'react';
import axios from 'axios';
import Swal from 'sweetalert2';
import localForage from 'localforage';
import PatientDashboardLayout from './PatientDashboardLayout';
import {useBaseUrl} from '../utils/utils';
import {useNavigate} from 'react-router-dom';

/**
 * Was `<p>Form coming soon...</p>` at App.tsx:141, routed at
 * /patient/service-requests/new. ServiceRequests.creator is a FK to Clinic,
 * not to Patient/Staff (uzima_collaborations/models.py) - a service request
 * is always issued *by a clinic*, so a patient submitting one here is really
 * asking a clinic of their choosing to raise it on their behalf. There's no
 * "my clinics" list wired up anywhere yet (PatientClinics.tsx is itself
 * still a tab-shell stub with no data), so this uses the existing
 * facilities/clinics listing endpoint for the picker instead of inventing a
 * new backend endpoint in the same pass.
 */

type ClinicOption = { clinic_id: number; clinic_name: string };

const ServiceRequestForm: React.FC = () => {
    const baseUrl = useBaseUrl();
    const navigate = useNavigate();
    const [clinics, setClinics] = useState<ClinicOption[]>([]);
    const [submitting, setSubmitting] = useState(false);
    const [form, setForm] = useState({
        title: '',
        description: '',
        creator_id: '',
        engagement_type: 'term',
        request_type: 'open',
        start_date: '',
        end_date: '',
        priority: 'normal',
    });

    useEffect(() => {
        document.title = "Request New Service - Uzima Healthcare";
        axios.post(`${baseUrl}facilities/clinics`, {}).then((res) => {
            setClinics((res.data?.data || []).map((c: any) => ({clinic_id: c.clinic_id, clinic_name: c.clinic_name})));
        }).catch(() => {
            // Non-fatal - the clinic dropdown just stays empty and the user
            // sees "No clinics available" rather than a broken page.
        });
    }, [baseUrl]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!form.creator_id) {
            Swal.fire('Choose a clinic', 'Select the clinic you want to raise this request with.', 'warning');
            return;
        }

        setSubmitting(true);
        try {
            const patientId = await localForage.getItem<string>('patient_id');
            const token = await localForage.getItem<string>('access_token');

            const response = await axios.post(`${baseUrl}collaborations/create-comprehensive-request/`, {
                ...form,
                creator_id: Number(form.creator_id),
                patient_id: patientId,
            }, {
                headers: {
                    Authorization: token ? `Bearer ${token}` : '',
                    'Content-Type': 'application/json',
                },
            });

            if (response.data.status === 'success' || response.data.request_id) {
                await Swal.fire('Request submitted', 'Your service request has been sent.', 'success');
                navigate('/patient/service-requests');
            } else {
                throw new Error(response.data.message || 'Could not submit request');
            }
        } catch (error: any) {
            Swal.fire('Error', error?.response?.data?.message || error.message || 'Could not submit request', 'error');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <PatientDashboardLayout>
            <div className="container-fluid">
                <div className="row mb-4">
                    <div className="col-12">
                        <h2 className="mb-1">Request New Service</h2>
                        <p className="text-muted">Ask a clinic to open a service request on your behalf</p>
                    </div>
                </div>

                <div className="row">
                    <div className="col-md-8">
                        <div className="card">
                            <div className="card-body">
                                <form onSubmit={handleSubmit}>
                                    <div className="mb-3">
                                        <label className="form-label">Clinic *</label>
                                        <select
                                            className="form-select"
                                            required
                                            value={form.creator_id}
                                            onChange={(e) => setForm({...form, creator_id: e.target.value})}
                                        >
                                            <option value="">Select a clinic...</option>
                                            {clinics.map((c) => (
                                                <option key={c.clinic_id} value={c.clinic_id}>{c.clinic_name}</option>
                                            ))}
                                        </select>
                                        {clinics.length === 0 && (
                                            <div className="form-text">No clinics available right now.</div>
                                        )}
                                    </div>

                                    <div className="mb-3">
                                        <label className="form-label">Title *</label>
                                        <input
                                            className="form-control"
                                            required
                                            value={form.title}
                                            onChange={(e) => setForm({...form, title: e.target.value})}
                                            placeholder="e.g., Cardiology consultation"
                                        />
                                    </div>

                                    <div className="mb-3">
                                        <label className="form-label">Description *</label>
                                        <textarea
                                            className="form-control"
                                            required
                                            rows={4}
                                            value={form.description}
                                            onChange={(e) => setForm({...form, description: e.target.value})}
                                            placeholder="What do you need help with?"
                                        />
                                    </div>

                                    <div className="row mb-3">
                                        <div className="col">
                                            <label className="form-label">Start date</label>
                                            <input
                                                type="date"
                                                className="form-control"
                                                value={form.start_date}
                                                onChange={(e) => setForm({...form, start_date: e.target.value})}
                                            />
                                        </div>
                                        <div className="col">
                                            <label className="form-label">End date</label>
                                            <input
                                                type="date"
                                                className="form-control"
                                                value={form.end_date}
                                                onChange={(e) => setForm({...form, end_date: e.target.value})}
                                            />
                                        </div>
                                    </div>

                                    <div className="mb-3">
                                        <label className="form-label">Priority</label>
                                        <select
                                            className="form-select"
                                            value={form.priority}
                                            onChange={(e) => setForm({...form, priority: e.target.value})}
                                        >
                                            <option value="low">Low</option>
                                            <option value="normal">Normal</option>
                                            <option value="high">High</option>
                                            <option value="urgent">Urgent</option>
                                        </select>
                                    </div>

                                    <button className="btn btn-primary" type="submit" disabled={submitting}>
                                        {submitting ? 'Submitting…' : 'Submit Request'}
                                    </button>
                                </form>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </PatientDashboardLayout>
    );
};

export default ServiceRequestForm;
